import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useAuthenticatedClient } from '@/api/queries'
import type { components } from '@/api/schema'

export type CustomerPayments = components['schemas']['QbCustomerPaymentsRead']
export type BankPaymentPolicy = CustomerPayments['bank_payment_policy']

export type CustomerInsights = components['schemas']['CustomerInsightsRead']

const customerPaymentsKey = (uuid: string) => ['customer-payments', uuid] as const

/** Sales, Net and Profit by month, what they buy and how they pay (USA-1305). */
export function useCustomerInsights(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: ['customer-insights', uuid],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/customer-groups/{uuid}/insights', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
  })
}

export function useCustomerPayments(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: customerPaymentsKey(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/customer-groups/{uuid}/payments', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
  })
}

/** What a policy means for this customer now: a clearing bank payment starts the project or waits. */
function proceeds(policy: BankPaymentPolicy, paidProjects: number): CustomerPayments['bank_payments'] {
  return policy === 'proceed' || (policy === 'automatic' && paidProjects > 0) ? 'proceed' : 'wait'
}

export function useUpdateBankPaymentPolicy(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const key = customerPaymentsKey(uuid)
  return useMutation({
    mutationFn: async (policy: BankPaymentPolicy) => {
      const client = await getClient()
      const { data, error } = await client.PUT('/dashboard/customer-groups/{uuid}/payments/bank-policy', {
        params: { path: { uuid } },
        body: { bank_payment_policy: policy },
      })
      if (error) throw error
      return data
    },
    onMutate: async (policy) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<CustomerPayments>(key)
      if (previous) {
        queryClient.setQueryData<CustomerPayments>(key, {
          ...previous,
          bank_payment_policy: policy,
          bank_payments: proceeds(policy, previous.paid_projects),
        })
      }
      return { previous }
    },
    onError: (_error, _policy, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
      toast.error('Could not save the bank payment setting')
    },
    onSuccess: (data) => queryClient.setQueryData(key, data),
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  })
}

